import { isAxiosError } from "axios";
import { ILoja } from "shared/interfaces/ILoja";
import { DiaSemana, ILojaPublica } from "shared/interfaces/ILojaPublica";
import { IConta } from "shared/interfaces/IConta";
import { IEnderecoLoja } from "shared/interfaces/IEnderecoLoja";
import { mapDadosBancarios } from "shared/mappers/dadosBancarios/dadosBancarios.mapper";
import { api } from "../api";
import { reaisParaCentavos } from "shared/utils/money";

export type StoreAddress = {
  zipCode: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  complement?: string;
  referencePoint?: string;
};

export type StoreCategory = {
  id: string;
  name: string;
};

export type LegalRepresentative = {
  addressId?: string | null;
  motherName?: string | null;
  monthlyIncome?: number | null;
  professionalOccupation?: string | null;
  selfDeclaredLegalRepresentative?: boolean | null;
};

export type BusinessHoursSchedule =
  NonNullable<ILoja["businessHoursSchedule"]>;

export type StoreBanking = {
  // Podem vir no GET, mas NÃO são enviados pelo frontend.
  holderName?: string | null;
  holderDocument?: string | null;
  bankCode?: string;
  branch?: string;
  branchDigit?: string | null;
  account?: string;
  accountDigit?: string | null;
  type?: string;
  transferInterval?: string | null;
  transferEnabled?: boolean;
  kycStatus?: string;
  kycStatusReason?: string | null;
  gatewayStatus?: string;
};

export type Store = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  cnpj: string;
  phone: string;
  email: string;
  ownerId: string;
  status: string;
  logoUrl: string | null;
  category: StoreCategory;
  address: StoreAddress;
  businessHours: string | null;
  businessHoursSchedule?: BusinessHoursSchedule | null;
  banking?: StoreBanking | null;
  rating?: {
    average: number;
    count: number;
  } | null;
  companyName?: string | null;
  annualRevenue?: number | null;
  legalRepresentative?: LegalRepresentative | null;

  // Compatibilidade com o ILoja atual.
  company_name?: string;
  annual_revenue?: number;
  mother_name?: string;
  monthly_income?: number;
  professional_occupation?: string;
  self_declared_legal_representative?: boolean;
};

const traduzirStatus = (
  status?: string
): ILoja["status"] => {
  switch (status) {
    case "ACTIVE":
      return "Ativo";
    case "PENDING":
      return "Pendente";
    case "BLOCKED":
      return "Bloqueado";
    case "INACTIVE":
      return "Inativo";
    default:
      return "Pendente";
  }
};

const mapearDadosBancarios = (
  banking?: StoreBanking | null
): IConta | null => {
  if (!banking) {
    return null;
  }

  return mapDadosBancarios({
    bankCode: banking.bankCode ?? "",
    branch: banking.branch ?? "",
    branchDigit: banking.branchDigit ?? null,
    account: banking.account ?? "",
    accountDigit: banking.accountDigit ?? null,
    type: banking.type ?? "",

    // Somente leitura da resposta da API.
    // Não são usados para montar POST/PATCH.
    holderName: banking.holderName ?? "",
    holderDocument: banking.holderDocument ?? "",

    kycStatus: banking.kycStatus,
    kycStatusReason: banking.kycStatusReason ?? null,
    gatewayStatus: banking.gatewayStatus,
  });
};

const mapearEndereco = (
  address?: StoreAddress | null
): IEnderecoLoja | undefined => {
  if (!address) {
    return undefined;
  }

  return {
    cep: address.zipCode,
    rua: address.street,
    numero: address.number,
    bairro: address.neighborhood,
    cidade: address.city,
    estado: address.state,
    complement: address.complement ?? "",
    referencePoint: address.referencePoint ?? "",
  };
};

const mapearLoja = (loja: Store): ILoja => {
  const legalRepresentative =
    loja.legalRepresentative;

  return {
    id: loja.id,
    ownerId: loja.ownerId,
    loja: loja.name ?? "",
    company_name:
      loja.companyName ??
      loja.company_name ??
      "",
    emailLoja: loja.email ?? "",
    telefone: loja.phone ?? "",
    cnpj: loja.cnpj ?? "",
    descricao: loja.description ?? "",
    logo: loja.logoUrl ?? "",
    segmento: loja.category?.id
      ? String(loja.category.id)
      : "",

    annual_revenue: Number(loja.annualRevenue ?? loja.annual_revenue ?? 0) / 100,

    mother_name:
      legalRepresentative?.motherName ??
      loja.mother_name ??
      "",

    monthly_income: Number(legalRepresentative?.monthlyIncome ?? loja.monthly_income ?? 0) / 100,

    professional_occupation:
      legalRepresentative?.professionalOccupation ??
      loja.professional_occupation ??
      "",

    self_declared_legal_representative:
      legalRepresentative
        ?.selfDeclaredLegalRepresentative ??
      loja.self_declared_legal_representative ??
      false,

    legalRepresentative:
      legalRepresentative
        ? {
            addressId:
              legalRepresentative.addressId ??
              undefined,
            motherName:
              legalRepresentative.motherName ??
              undefined,
            monthlyIncome:
              legalRepresentative.monthlyIncome != null? legalRepresentative.monthlyIncome / 100
            : undefined,
            professionalOccupation:
              legalRepresentative.professionalOccupation ??
              undefined,
            selfDeclaredLegalRepresentative:
              legalRepresentative.selfDeclaredLegalRepresentative ??
              undefined,
          }
        : undefined,

    endereco: mapearEndereco(loja.address),
    status: traduzirStatus(loja.status),
    horario: loja.businessHours ?? undefined,
    businessHoursSchedule:
      loja.businessHoursSchedule ?? undefined,
    dadosBancarios:
      mapearDadosBancarios(loja.banking),
  };
};

export type CriarNovaLojaAddressPayload = {
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  complement?: string;
  referencePoint?: string;
};

export type CriarNovaLojaLegalRepresentativePayload = {
  addressId?: string;
  motherName: string;
  monthlyIncome: number;
  professionalOccupation: string;
  selfDeclaredLegalRepresentative: boolean;
};

export type CriarNovaLojaBankingPayload = {
  bankCode: string;
  branch: string;
  branchDigit: string | null;
  account: string;
  accountDigit: string | null;
  type: "checking" | "savings";
  transferInterval?: string;
};

export interface CriarNovaLojaPayload {
  name: string;
  companyName: string;
  cnpj: string;
  phone: string;
  email: string;
  categoryId: string;
  description?: string;
  annualRevenue: number;
  legalRepresentative:
    CriarNovaLojaLegalRepresentativePayload;
  address: CriarNovaLojaAddressPayload;
  businessHours:
    | string
    | BusinessHoursSchedule;
  banking: CriarNovaLojaBankingPayload;
}

export const criarNovaLoja = async (
  dadosLoja: CriarNovaLojaPayload
): Promise<string> => {
  if (!dadosLoja) {
    throw new Error(
      "Dados da loja não informados."
    );
  }

  const name = dadosLoja.name?.trim();
  const companyName =
    dadosLoja.companyName?.trim();
  const cnpj =
    dadosLoja.cnpj?.replace(/\D/g, "");
  const phone =
    dadosLoja.phone?.replace(/\D/g, "");
  const email = dadosLoja.email?.trim();
  const categoryId =
    dadosLoja.categoryId?.trim();
  const description =
    dadosLoja.description?.trim() ?? "";
  const annualRevenue = reaisParaCentavos(Number(dadosLoja.annualRevenue));

  const legalRepresentative = {
    ...(dadosLoja.legalRepresentative.addressId
      ? {
          addressId:
            dadosLoja.legalRepresentative
              .addressId,
        }
      : {}),
    motherName:
      dadosLoja.legalRepresentative.motherName?.trim(),
    monthlyIncome: reaisParaCentavos(Number(dadosLoja.legalRepresentative.monthlyIncome)),
    professionalOccupation:
      dadosLoja.legalRepresentative
        .professionalOccupation?.trim(),
    selfDeclaredLegalRepresentative:
      Boolean(
        dadosLoja.legalRepresentative
          .selfDeclaredLegalRepresentative
      ),
  };

  const address = {
    street:
      dadosLoja.address.street?.trim(),
    number:
      dadosLoja.address.number?.trim(),
    neighborhood:
      dadosLoja.address.neighborhood?.trim(),
    city:
      dadosLoja.address.city?.trim(),
    state:
      dadosLoja.address.state?.trim(),
    zipCode:
      dadosLoja.address.zipCode?.replace(
        /\D/g,
        ""
      ),
    complement:
      dadosLoja.address.complement?.trim() ?? "",
    referencePoint:
      dadosLoja.address.referencePoint?.trim() ??
      "",
  };

  const businessHours =
    typeof dadosLoja.businessHours === "string"
      ? dadosLoja.businessHours.trim()
      : dadosLoja.businessHours;

  // IMPORTANTE:
  // Não incluir holderName nem holderDocument.
  const banking = {
    bankCode: String(
      dadosLoja.banking.bankCode
    )
      .replace(/\D/g, "")
      .padStart(3, "0"),

    branch:
      dadosLoja.banking.branch?.trim(),

    branchDigit:
      dadosLoja.banking.branchDigit ?? null,

    account:
      dadosLoja.banking.account?.trim(),

    accountDigit:
      dadosLoja.banking.accountDigit ?? null,

    type: dadosLoja.banking.type,

    ...(dadosLoja.banking.transferInterval
      ? {
          transferInterval:
            dadosLoja.banking.transferInterval,
        }
      : {}),
  };

  if (!name) {
    throw new Error(
      "Nome da loja não informado."
    );
  }

  if (!companyName) {
    throw new Error(
      "Razão Social não informada."
    );
  }

  if (!cnpj) {
    throw new Error(
      "CNPJ da loja não informado."
    );
  }

  if (cnpj.length !== 14) {
    throw new Error(
      "CNPJ deve possuir 14 números."
    );
  }

  if (!phone) {
    throw new Error(
      "Telefone da loja não informado."
    );
  }

  if (!email) {
    throw new Error(
      "E-mail da loja não informado."
    );
  }

  if (!categoryId) {
    throw new Error(
      "Categoria da loja não informada."
    );
  }

  if (
    !Number.isFinite(annualRevenue) ||
    annualRevenue <= 0
  ) {
    throw new Error(
      "Ganhos anuais devem ser maiores que zero."
    );
  }

  if (!Number.isInteger(annualRevenue)) {
    throw new Error(
      "Ganhos anuais devem ser um número inteiro."
    );
  }

  if (!legalRepresentative.motherName) {
    throw new Error(
      "Nome da mãe do representante legal não informado."
    );
  }

  if (
    !Number.isFinite(
      legalRepresentative.monthlyIncome
    ) ||
    legalRepresentative.monthlyIncome <= 0
  ) {
    throw new Error(
      "Ganhos mensais devem ser maiores que zero."
    );
  }

  if (
    !legalRepresentative.professionalOccupation
  ) {
    throw new Error(
      "Ocupação profissional não informada."
    );
  }

  if (
    !legalRepresentative
      .selfDeclaredLegalRepresentative
  ) {
    throw new Error(
      "É necessário confirmar a responsabilidade como representante legal."
    );
  }

  if (!address.street) {
    throw new Error(
      "Endereço da loja não informado."
    );
  }

  if (!address.number) {
    throw new Error(
      "Número do endereço não informado."
    );
  }

  if (!address.neighborhood) {
    throw new Error(
      "Bairro do endereço não informado."
    );
  }

  if (!address.city) {
    throw new Error(
      "Cidade da loja não informada."
    );
  }

  if (!address.state) {
    throw new Error(
      "Estado da loja não informado."
    );
  }

  if (!address.zipCode) {
    throw new Error(
      "CEP da loja não informado."
    );
  }

  if (
    typeof businessHours === "string" &&
    !businessHours
  ) {
    throw new Error(
      "Horário de funcionamento não informado."
    );
  }

  if (!banking.bankCode) {
    throw new Error(
      "Código do banco não informado."
    );
  }

  if (!banking.branch) {
    throw new Error(
      "Agência não informada."
    );
  }

  if (!banking.account) {
    throw new Error(
      "Conta não informada."
    );
  }

  if (
    banking.type !== "checking" &&
    banking.type !== "savings"
  ) {
    throw new Error(
      "Tipo de conta inválido."
    );
  }

  const payload = {
    name,
    companyName,
    cnpj,
    phone,
    email,
    categoryId,
    description,
    annualRevenue,
    legalRepresentative,
    address,
    businessHours,
    banking,
  };

  const response = await api.post<{
    id: string;
    name?: string;
    slug?: string;
    status?: string;
  }>("/stores", payload);

  if (!response.data?.id) {
    throw new Error(
      "A API criou a loja, mas não retornou o ID."
    );
  }

  return response.data.id;
};

export const buscarLojasPorUsuario =
  async (): Promise<ILoja[]> => {
    const response =
      await api.get<Store[]>(
        "/stores/mine"
      );

    return response.data.map(mapearLoja);
  };

export const buscarLojaPorId = async (
  id: string
): Promise<ILoja | null> => {
  if (!id || id === "undefined") {
    return null;
  }

  const response =
    await api.get<Store>(
      `/stores/mine/${id}`
    );

  return mapearLoja(response.data);
};

/* ================================
   VITRINE PÚBLICA (GET /stores/:id)
================================ */

type PublicStore = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  phone: string;
  logoUrl: string | null;
  category: StoreCategory;
  address: StoreAddress;
  businessHoursSchedule?: Partial<ILojaPublica["horarios"]> | null;
  rating?: {
    average: number | null;
    count: number;
  } | null;
};

const DIAS_SEMANA: DiaSemana[] = [
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
  "SUN",
];

const mapearLojaPublica = (loja: PublicStore): ILojaPublica => ({
  id: loja.id,
  nome: loja.name ?? "",
  slug: loja.slug ?? "",
  descricao: loja.description ?? null,
  telefone: loja.phone ?? "",
  logo: loja.logoUrl ?? null,
  categoria: {
    id: loja.category?.id ?? "",
    nome: loja.category?.name ?? "",
  },
  endereco: {
    rua: loja.address?.street ?? "",
    numero: loja.address?.number ?? "",
    bairro: loja.address?.neighborhood ?? "",
    cidade: loja.address?.city ?? "",
    estado: loja.address?.state ?? "",
  },
  horarios: DIAS_SEMANA.reduce(
    (horarios, dia) => ({
      ...horarios,
      [dia]: loja.businessHoursSchedule?.[dia] ?? null,
    }),
    {} as ILojaPublica["horarios"]
  ),
  avaliacao: {
    // null = sem avaliações; não é o mesmo que nota zero.
    media: loja.rating?.average ?? null,
    total: loja.rating?.count ?? 0,
  },
});

/** Loja vista pelo comprador. Retorna null quando ela não existe (404). */
export const buscarLojaPublica = async (
  id: string
): Promise<ILojaPublica | null> => {
  if (!id || id === "undefined") {
    return null;
  }

  try {
    const response =
      await api.get<PublicStore>(
        `/stores/${id}`
      );

    return mapearLojaPublica(response.data);
  } catch (error) {
    if (
      isAxiosError(error) &&
      (error.response?.status === 404 ||
        error.response?.status === 400)
    ) {
      return null;
    }

    throw error;
  }
};

/**
 * Lojas pelo nome, para a busca (GET /search/stores). Ignora acentos:
 * "cafe" encontra "Café". Termos com menos de 2 letras não buscam.
 */
export const buscarLojas = async (
  termo: string,
  limite = 10
): Promise<ILojaPublica[]> => {
  const q = termo.trim();

  if (q.length < 2) {
    return [];
  }

  const response =
    await api.get<PublicStore[]>(
      "/search/stores",
      { params: { q, limit: limite } }
    );

  return Array.isArray(response.data)
    ? response.data.map(mapearLojaPublica)
    : [];
};

export type BankingUpdatePayload = {
  bankCode: string;
  branch: string;
  branchDigit: string | null;
  account: string;
  accountDigit: string | null;
  type: "checking" | "savings";
  transferInterval?: string;
  transferEnabled?: boolean;
};

export type LegalRepresentativeUpdatePayload = {
  addressId?: string;
  motherName: string;
  monthlyIncome: number;
  professionalOccupation: string;
  selfDeclaredLegalRepresentative: boolean;
};

export interface AtualizarLojaPayload {
  name?: string;
  cnpj?: string;
  phone?: string;
  email?: string;
  categoryId?: string;
  description?: string;
  companyName?: string;
  annualRevenue?: number;
  legalRepresentative?:
    LegalRepresentativeUpdatePayload;
  address?: {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
    referencePoint?: string;
    complement?: string;
  };
  businessHours?:
    | string
    | BusinessHoursSchedule;
  banking?: BankingUpdatePayload;
}

export const atualizarLoja = async (
  id: string,
  dados: AtualizarLojaPayload
): Promise<Store> => {
  if (!id || id === "undefined") {
    throw new Error(
      "ID da loja não informado para atualização."
    );
  }

  if (!dados) {
    throw new Error(
      "Dados da loja não informados."
    );
  }

  const response =
    await api.patch<Store>(
      `/stores/${id}`,
      dados
    );

  return response.data;
};

export const atualizarDadosBancarios =
  atualizarLoja;

export interface ICategoriaLoja {
  id: string;
  name: string;
  slug: string;
}

export const buscarCategoriasLoja =
  async (): Promise<ICategoriaLoja[]> => {
    const response =
      await api.get<ICategoriaLoja[]>(
        "/stores/categories"
      );

    return response.data;
  };

export const desativarLoja = async (
  id: string
): Promise<void> => {
  if (!id) {
    throw new Error(
      "ID da loja não informado."
    );
  }

  await api.delete(`/stores/${id}`);
};

export const recuperarLoja = async (
  id: string
): Promise<void> => {
  if (!id) {
    throw new Error(
      "ID da loja não informado."
    );
  }

  await api.post(
    `/stores/${id}/recover`
  );
};

export const uploadImagemLoja = async (
  lojaId: string | undefined,
  file: File
): Promise<void> => {
  if (!lojaId) {
    throw new Error(
      "ID da loja não informado."
    );
  }

  if (!file) {
    throw new Error(
      "Arquivo da logo não informado."
    );
  }

  const extensao = file.name
    .split(".")
    .pop()
    ?.toLowerCase();

  if (
    !extensao ||
    !["jpg", "jpeg", "png", "webp"].includes(
      extensao
    )
  ) {
    throw new Error(
      "Formato inválido. Use jpg, jpeg, png ou webp."
    );
  }

  const response = await api.post(
    `/stores/${lojaId}/logo/upload-url`,
    {
      fileExtension: extensao,
    }
  );

  const { uploadUrl, key } =
    response.data;

  if (!uploadUrl || !key) {
    throw new Error(
      "A API não retornou a URL de upload ou a chave da imagem."
    );
  }

  const uploadResponse = await fetch(
    uploadUrl,
    {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    }
  );

  if (!uploadResponse.ok) {
    throw new Error(
      "Erro ao enviar a imagem para o armazenamento."
    );
  }

  await api.patch(
    `/stores/${lojaId}/logo`,
    { key }
  );
};